/**
 * PartnerScope context - the partner subtree the admin is "acting under".
 *
 * Background: every admin has access to ≥1 partner subtree (via role
 * grants). A super-admin can drive the whole tree; a wl-admin on
 * `platform.finterra` can only touch partners under that path. When
 * they're looking at the Users page, the scope determines the default
 * filter. When they flip the partner switcher, all pages re-scope.
 *
 * Source of truth: the profile's `roles` array. The switcher writes
 * the selected partner into localStorage (`finterra.admin.scope`) so
 * the choice persists across reloads.
 *
 * Hook + context live in a module separate from the provider component
 * so eslint-plugin-react-refresh doesn't complain about mixed exports.
 */

import { createContext, useContext } from "react";

import type { BrandConfig, PartnerSummary } from "@tilt/admin-sdk";

export interface PartnerScopeValue {
  /** The active scope partner. Always a partner the caller has a role on. */
  scope: PartnerSummary;
  /** All partners the caller can switch into (derived from profile.roles). */
  available: PartnerSummary[];
  /** Switcher handler - updates context + writes to localStorage. */
  setScope: (partner: PartnerSummary) => void;
  /**
   * Brand applied to portal chrome. Sourced from the caller's
   * primary_partner - stable for the session so scope-switching doesn't
   * re-skin the shell. (Scope drives data filters; brand represents
   * "which workspace am I operating inside".)
   */
  brand: BrandConfig;
}

export const PartnerScopeContext = createContext<PartnerScopeValue | null>(null);

export function usePartnerScope(): PartnerScopeValue {
  const ctx = useContext(PartnerScopeContext);
  if (!ctx) {
    throw new Error("usePartnerScope must be used under <PartnerScopeProvider>");
  }
  return ctx;
}
